import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ActionsMenu, type BaseFormAction } from "@/base/ActionsMenu";
import { listAccountBonds, type AccountDetail } from "@/lib/brokerAccounts";
import {
  issuedBondChoicesFromAccountBonds,
  type IssuedBondChoice,
} from "@/lib/bondServicing";
import { messageFor } from "@/lib/errors";
import {
  getBondSubmission,
  getResolvedBondConfig,
  type BondFinalField,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { RequestRiderDialog } from "@/components/bond-submission/RequestRiderDialog";
import { ReportClaimDialog } from "@/components/bond-submission/ReportClaimDialog";
import { SendApplicationDialog } from "@/components/surety-account/SendApplicationDialog";

// The Surety account header's Actions (BRK-S398, the dialogs BRK-S412).
//
// The four items and their lines are the October wireframe's (BRK-S440).
//
// - Start bond stays a link into the bond wizard — no second capture surface
//   for its fields. It is never greyed here: whether an insured with no
//   program may start a bid is the wizard's served answer, not a menu rule.
// - Send application, Rider and File claim open their dialogs. The rider and
//   claim dialogs offer the insured's issued bonds, read from the account's
//   Bonds route the first time either opens.
// - Rider draws the chosen bond's details as the submission page does
//   (BRK-S495): each bond chosen reads its submission and configuration, and
//   the dialog sends the changed `values` with the request.

type Open = "application" | "rider" | "claim" | null;

export function SuretyAccountActions({ detail }: { detail: AccountDetail }) {
  const navigate = useNavigate();
  const { authedRequest } = useAuth();
  const [open, setOpen] = useState<Open>(null);
  const [bonds, setBonds] = useState<IssuedBondChoice[] | null>(null);
  const [bondsError, setBondsError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);

  const approved = detail.preapproval?.status === "approved";
  const held = approved ? (detail.preapproval?.programType ?? null) : null;

  async function readBonds() {
    if (bonds !== null || reading) return;
    setReading(true);
    setBondsError(null);
    try {
      const { bonds: issued } = await listAccountBonds(
        authedRequest,
        detail.id,
      );
      setBonds(issuedBondChoicesFromAccountBonds(issued));
    } catch (err) {
      // Forgotten on failure, so the next open reads again.
      setBondsError(messageFor(err));
    } finally {
      setReading(false);
    }
  }

  function openWithBonds(which: "rider" | "claim") {
    setOpen(which);
    void readBonds();
  }

  const actions: BaseFormAction[] = [
    {
      id: "start-bond",
      label: "Start bond",
      description: "Begin a new bid, P&P or maintenance bond",
      onSelect: () =>
        navigate(
          `/surety/construction?accountId=${encodeURIComponent(detail.id)}`,
        ),
    },
    {
      id: "send-application",
      label: "Send application",
      description: "Email the surety application to the client",
      onSelect: () => setOpen("application"),
    },
    {
      id: "rider",
      label: "Rider",
      description: "Change an issued bond: amount, names or dates",
      onSelect: () => openWithBonds("rider"),
    },
    {
      id: "claim",
      label: "File claim",
      description: "Report a claim against an issued bond",
      onSelect: () => openWithBonds("claim"),
    },
  ];

  // The rider's chosen bond, its order and its configuration (BRK-S495).
  const [riderFor, setRiderFor] = useState<string | null>(null);
  const [riderOrder, setRiderOrder] = useState<BondSubmission | undefined>();
  const [riderFields, setRiderFields] = useState<
    readonly BondFinalField[] | null
  >(null);
  const [riderLoading, setRiderLoading] = useState(false);
  const [riderFailed, setRiderFailed] = useState(false);
  useEffect(() => {
    setRiderOrder(undefined);
    setRiderFields(null);
    setRiderFailed(false);
    if (open !== "rider" || riderFor === null) {
      setRiderLoading(false);
      return;
    }
    let cancelled = false;
    setRiderLoading(true);
    (async () => {
      try {
        const order = await getBondSubmission(authedRequest, riderFor);
        // A failed config read is not fatal: the request is still takeable
        // in Details alone, as on the submission page.
        const fields = await getResolvedBondConfig(
          authedRequest,
          order.subproductId,
        ).then(
          (config) => config.finalStep.fields,
          () => null,
        );
        if (cancelled) return;
        setRiderOrder(order);
        setRiderFields(fields);
      } catch {
        if (!cancelled) setRiderFailed(true);
      } finally {
        if (!cancelled) setRiderLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, open, riderFor]);

  const close = () => setOpen(null);
  return (
    <>
      <ActionsMenu actions={actions} />
      <SendApplicationDialog
        open={open === "application"}
        onClose={close}
        accountId={detail.id}
        defaultEmail={detail.emailAddress}
        heldProgram={held}
      />
      <RequestRiderDialog
        open={open === "rider"}
        onClose={close}
        bonds={bonds}
        bondsError={bondsError}
        order={riderOrder}
        fields={riderFields}
        onBondChange={setRiderFor}
        detailsLoading={riderLoading}
        detailsUnavailable={riderFailed}
      />
      <ReportClaimDialog
        open={open === "claim"}
        onClose={close}
        bonds={bonds}
        bondsError={bondsError}
      />
    </>
  );
}
